import {
  deriveResidentDocumentStatusLabel,
  getResidentDocumentsForType,
} from '@/features/resident/utils/residentDocuments'
import { reimbursementTypeDisplayLabel } from '@/features/resident/utils/residentHeaderStatus'
import type {
  ResidentDefaultFormValues,
  ResidentDocumentGroup,
} from '@/features/resident/types'
import type {
  ReimbursementDocumentStatus,
  ReimbursementStatusBarProps,
} from '../types/components'
import type {
  ReimbursementDocumentType,
  ReimbursementDocumentsMap,
  ReimbursementExtendedFields,
} from '../types/reimbursementFormTypes'
import { EMPTY_REIMBURSEMENT_EXTENDED_FIELDS } from '../types/reimbursementFormTypes'
import { milestoneHasReviewIncompleteDocument } from './reimbursementMilestoneStatus'

const EMPTY_DOC: ReimbursementDocumentStatus = { uploaded: false, status: 'pending' }

function normalizeDocumentStatusLabel(label: string): ReimbursementDocumentStatus['status'] {
  const normalized = String(label ?? '').trim().toLowerCase()
  if (!normalized || normalized === 'missing') return 'pending'
  if (['received', 'approved', 'complete', 'completed', 'paid'].includes(normalized)) {
    return 'approved'
  }
  if (['incomplete', 'rejected', 'denied'].includes(normalized)) {
    return 'incomplete'
  }
  if (['uploaded', 'pending'].includes(normalized)) {
    return 'uploaded'
  }
  return 'uploaded'
}

export function toReimbursementDocumentStatus(
  fileValue: unknown,
  statusValue: unknown,
  documents?: ResidentDocumentGroup | null,
  fileType?: string,
): ReimbursementDocumentStatus {
  const hasFormFile = Boolean(String(fileValue ?? '').trim())
  const documentRows = fileType ? getResidentDocumentsForType(documents, fileType) : []
  const hasUploadedFile = hasFormFile || documentRows.length > 0

  let statusLabel = String(statusValue ?? '').trim()
  if (!statusLabel && documentRows.length) {
    statusLabel = deriveResidentDocumentStatusLabel(documentRows)
  }

  if (!hasUploadedFile) {
    return { uploaded: false, status: 'pending' }
  }

  return {
    uploaded: true,
    status: normalizeDocumentStatusLabel(statusLabel),
  }
}

function formatFirstMonthRentUnpaid(values: ResidentDefaultFormValues): string {
  const month = String(values.first_month_not_paid_month ?? '').trim()
  const year = String(values.first_month_not_paid_year ?? '').trim()
  if (!month || !year) return ''
  return `${month}/${year}`
}

function formatSecurityDeposit(value: unknown): string {
  const raw = String(value ?? '').trim()
  if (!raw || raw === '$0.00' || raw === '0' || raw === '0.00') return ''
  return raw
}

function inferSecurityDepositCollected(values: ResidentDefaultFormValues): boolean {
  const amount = formatSecurityDeposit(values.unused_security_deposit)
  return Boolean(amount)
}

export interface BuildReimbursementStatusBarPropsArgs {
  values: ResidentDefaultFormValues
  residentDocuments?: ResidentDocumentGroup | null
  extended?: ReimbursementExtendedFields
  localDocuments?: ReimbursementDocumentsMap
}

function localDocToStatus(
  doc: ReimbursementDocumentsMap[ReimbursementDocumentType] | null | undefined,
): ReimbursementDocumentStatus {
  if (!doc) return EMPTY_DOC
  return {
    uploaded: true,
    status: doc.status ?? 'uploaded',
  }
}

export function buildReimbursementStatusBarProps({
  values,
  residentDocuments,
  extended = EMPTY_REIMBURSEMENT_EXTENDED_FIELDS,
  localDocuments,
}: BuildReimbursementStatusBarPropsArgs): ReimbursementStatusBarProps {
  const currentLedgerStatus = toReimbursementDocumentStatus(
    values.payment_ledger,
    values.payment_ledger_status,
    residentDocuments,
    'payment_ledger',
  )
  const finalLedgerFromApi = toReimbursementDocumentStatus(
    values.payment_ledger,
    values.payment_ledger_status,
    residentDocuments,
    'final_payment_ledger',
  )

  const signedLease =
    localDocuments?.signed_lease != null
      ? localDocToStatus(localDocuments.signed_lease)
      : toReimbursementDocumentStatus(
          values.lease_agreement,
          values.lease_agreement_status,
          residentDocuments,
          'lease_agreement',
        )

  const currentLedger =
    localDocuments?.current_ledger != null
      ? localDocToStatus(localDocuments.current_ledger)
      : currentLedgerStatus

  const finalLedger =
    localDocuments?.final_ledger != null
      ? localDocToStatus(localDocuments.final_ledger)
      : finalLedgerFromApi

  const skipAffidavit =
    localDocuments?.skip_affidavit != null
      ? localDocToStatus(localDocuments.skip_affidavit)
      : toReimbursementDocumentStatus(
          values.skip_claim_affidavit,
          values.skip_claim_affidavit_status,
          residentDocuments,
          'skip_claim_affidavit',
        )

  const evictionLawsuit =
    localDocuments?.eviction_lawsuit != null
      ? localDocToStatus(localDocuments.eviction_lawsuit)
      : toReimbursementDocumentStatus(
          values.eviction_lawsuit,
          values.unlawful_detainer_status,
          residentDocuments,
          'eviction_lawsuit',
        )

  const securityDepositCollected =
    extended.security_deposit_collected !== null
      ? extended.security_deposit_collected
      : inferSecurityDepositCollected(values)

  return {
    layout: 'vertical',
    firstMonthRentUnpaid: formatFirstMonthRentUnpaid(values),
    reimbursementReqDate: String(values.rental_status_date ?? '').trim(),
    reimbursementType: reimbursementTypeDisplayLabel(values.reimbursement_type),
    reason: String(values.reason_for_default ?? '').trim(),
    signedLease,
    currentLedger,
    finalLedger,
    skipAffidavit,
    evictionLawsuit,
    alternateCourtDocument: localDocToStatus(localDocuments?.alternate_court_document),
    caresActNotice: localDocToStatus(localDocuments?.cares_act_notice),
    bankruptcyDocument: localDocToStatus(localDocuments?.bankruptcy_document),
    courtRegistry: localDocToStatus(localDocuments?.court_registry),
    stipulationAgreement: localDocToStatus(localDocuments?.stipulation_agreement),
    defaultStipulation: localDocToStatus(localDocuments?.default_stipulation),
    bondRegistryReceipt: localDocToStatus(localDocuments?.bond_registry_receipt),
    caresActApplicable: extended.cares_act_applicable,
    bankruptcyApplicable: extended.bankruptcy_applicable,
    appealApplicable: extended.appeal_applicable,
    stipulationApplicable: extended.stipulation_applicable,
    alternateCourtDocApplicable: extended.alternate_court_document_applicable,
    usingThirdPartyEvictionServices: extended.using_third_party_eviction_services,
    utilizingLegalTeamForEviction: extended.utilizing_legal_team_for_eviction,
    thirdPartyServicerName: extended.third_party_servicer_name,
    residentPaidIntoCourt: extended.resident_paid_into_court,
    residentDefaultedOnRepayment: extended.resident_defaulted_on_repayment,
    securityDepositCollected,
    securityDeposit: formatSecurityDeposit(values.unused_security_deposit),
    leaseModificationsAtMoveIn: extended.lease_modifications_at_move_in,
    skippedDuringEviction: extended.skipped_during_eviction,
    evictionLawsuitFiledBeforeMoveout: null,
    tenantVacatedBeforeFiling: extended.skipped_during_eviction,
    originalLease: localDocToStatus(localDocuments?.original_lease),
    originalLedger: localDocToStatus(localDocuments?.original_ledger),
    moveOutDate: String(values.moveout_date ?? '').trim(),
    newResidentLeaseStartDate: String(values.new_resident_lease_start_date ?? '').trim(),
    newResidentMoveInDate: String(values.movein_date ?? '').trim(),
    paymentInfoConfirmed: extended.payment_info_confirmed,
  }
}

export function getCoreMilestoneStatus(
  props: ReimbursementStatusBarProps,
): 'complete' | 'partial' | 'pending' | 'rejected' {
  if (milestoneHasReviewIncompleteDocument(props, 1)) {
    return 'rejected'
  }

  const isCoreDetailsComplete = !!(
    props.firstMonthRentUnpaid &&
    props.reimbursementReqDate &&
    props.reimbursementType &&
    props.securityDepositCollected !== null &&
    (props.securityDepositCollected !== true || !!props.securityDeposit) &&
    props.leaseModificationsAtMoveIn !== null &&
    (props.leaseModificationsAtMoveIn === false ||
      (props.originalLease.uploaded && props.originalLease.status === 'approved')) &&
    props.signedLease.uploaded &&
    props.signedLease.status === 'approved' &&
    props.currentLedger.uploaded &&
    props.currentLedger.status === 'approved'
  )

  if (isCoreDetailsComplete) return 'complete'

  if (
    props.firstMonthRentUnpaid ||
    props.reimbursementReqDate ||
    props.reimbursementType ||
    props.securityDepositCollected !== null ||
    props.leaseModificationsAtMoveIn !== null ||
    props.signedLease.uploaded ||
    props.currentLedger.uploaded
  ) {
    return 'partial'
  }

  return 'pending'
}
